import React, { useState, useEffect } from 'react';
import { subCategoriesApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, Tag, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PARENT_CATEGORIES, CATEGORY_LABELS, CATEGORY_BADGE_CLASS } from '@/constants/categories';

export default function SubCategoriesPage() {
  const { user } = useAuth();
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', parent_category: 'material' });
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');

  const isEditor = ['admin', 'manager'].includes(user?.role);

  const fetchSubCategories = async () => {
    try {
      const { data } = await subCategoriesApi.list();
      setSubCategories(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchSubCategories(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await subCategoriesApi.create({ name: form.name.trim(), parent_category: form.parent_category });
      toast.success(`Sub-category "${form.name.trim()}" created`);
      setShowForm(false);
      setForm({ name: '', parent_category: 'material' });
      fetchSubCategories();
    } catch {
      toast.error('Failed to create sub-category');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await subCategoriesApi.delete(id);
      toast.success(`"${name}" deleted`);
      fetchSubCategories();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = subCategories.filter(sc => {
    if (filterCat && sc.parent_category !== filterCat) return false;
    if (search && !sc.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const materialCount = subCategories.filter(sc => sc.parent_category === 'material').length;
  const serviceCount = subCategories.filter(sc => sc.parent_category === 'service').length;
  const miscCount = subCategories.filter(sc => sc.parent_category === 'miscellaneous').length;
  const assetsCount = subCategories.filter(sc => sc.parent_category === 'assets').length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="sub-categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Sub-Categories</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {materialCount} materials, {serviceCount} services{miscCount > 0 ? `, ${miscCount} misc` : ''}{assetsCount > 0 ? `, ${assetsCount} assets` : ''}
          </p>
        </div>
        {isEditor && (
          <Button className="gap-1.5" onClick={() => { setForm({ name: '', parent_category: 'material' }); setShowForm(true); }} data-testid="add-sub-category-btn">
            <Plus className="w-4 h-4" /> Add Sub-Category
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={filterCat || '__all__'} onValueChange={v => setFilterCat(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-parent-category">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Types</SelectItem>
            <SelectItem value="material">Material</SelectItem>
            <SelectItem value="service">Service</SelectItem>
            <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
            <SelectItem value="assets">Assets</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            className="h-8 text-xs pl-8"
            data-testid="search-sub-categories"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border overflow-x-auto" data-testid="sub-categories-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Name</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Type</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Created</th>
              {isEditor && <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isEditor ? 4 : 3} className="text-center py-12 text-muted-foreground">
                <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No sub-categories found
              </td></tr>
            ) : filtered.map(sc => (
              <tr key={sc._id} className="border-b border-border hover:bg-muted/50 transition-colors" data-testid={`sub-cat-row-${sc._id}`}>
                <td className="py-3 px-4 font-medium">{sc.name}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-medium px-2 py-0.5 ${CATEGORY_BADGE_CLASS[sc.parent_category] || 'bg-gray-50 dark:bg-gray-950/30 text-gray-700 dark:text-gray-400'}`}>
                    {CATEGORY_LABELS[sc.parent_category] || sc.parent_category}
                  </span>
                </td>
                <td className="py-3 px-4 text-muted-foreground text-xs">{sc.created_at ? new Date(sc.created_at).toLocaleDateString() : '-'}</td>
                {isEditor && (
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => handleDelete(sc._id, sc.name)} className="p-1.5 hover:bg-muted rounded text-red-600" data-testid={`delete-sub-cat-${sc._id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Sub-Category</DialogTitle>
            <DialogDescription>Create a new sub-category type</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="sub-category-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Parent Category</Label>
              <Select value={form.parent_category} onValueChange={v => setForm(f => ({ ...f, parent_category: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="sub-cat-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PARENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={{ material: 'e.g., Cement, Steel, Tiles', service: 'e.g., Plumbing, Electrical', miscellaneous: 'e.g., Stationery, Travel, Food', assets: 'e.g., Vehicle, Machinery, Furniture' }[form.parent_category] || 'Enter name'}
                required
                className="mt-1.5"
                data-testid="sub-cat-name-input"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" data-testid="sub-cat-submit-btn">Create Sub-Category</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
